import { invokeFunction, invokePublicFunction } from "../api";
import { toApiRequestError } from "../errors";

export interface FamilyStatusPortalResponse {
  share_link_id: string;
  facility_name: string;
  survey_date: string;
  status_code: string;
  status_label: string;
  status_message: string;
  updated_at: string;
  expires_at: string;
  poll_interval_seconds: number;
}

export interface IssuedFamilyStatusLink {
  share_link_id: string;
  token: string;
  path: string;
  expires_at: string;
  status_code: string;
  status_label: string;
}

function asRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}

export async function getPublicFamilyStatus(
  token: string,
): Promise<FamilyStatusPortalResponse> {
  const { data, error } = await invokePublicFunction<unknown>("family-status", {
    method: "GET",
    path: `/${encodeURIComponent(token)}`,
  });

  if (error) throw toApiRequestError(error, "Failed to load family status");
  if (
    !asRecord(data) ||
    typeof data.share_link_id !== "string" ||
    typeof data.status_code !== "string" ||
    typeof data.status_label !== "string" ||
    typeof data.status_message !== "string"
  ) {
    throw new Error("Malformed family status response");
  }

  return {
    share_link_id: data.share_link_id,
    facility_name:
      typeof data.facility_name === "string" ? data.facility_name : "",
    survey_date: typeof data.survey_date === "string" ? data.survey_date : "",
    status_code: data.status_code,
    status_label: data.status_label,
    status_message: data.status_message,
    updated_at: typeof data.updated_at === "string" ? data.updated_at : "",
    expires_at: typeof data.expires_at === "string" ? data.expires_at : "",
    poll_interval_seconds:
      typeof data.poll_interval_seconds === "number"
        ? data.poll_interval_seconds
        : 30,
  };
}

export async function issueFamilyStatusLink(
  visitId: string,
  contactId?: string,
): Promise<IssuedFamilyStatusLink> {
  const { data, error } = await invokeFunction<unknown>("family-status", {
    method: "POST",
    path: "/issue",
    body: {
      visit_id: visitId,
      contact_id: contactId ?? null,
      expires_in_hours: 24,
    },
  });

  if (error) throw toApiRequestError(error, "Failed to issue family status link");
  if (
    !asRecord(data) ||
    typeof data.share_link_id !== "string" ||
    typeof data.token !== "string" ||
    typeof data.path !== "string" ||
    typeof data.expires_at !== "string"
  ) {
    throw new Error("Malformed issued family status link response");
  }

  return {
    share_link_id: data.share_link_id,
    token: data.token,
    path: data.path,
    expires_at: data.expires_at,
    status_code: typeof data.status_code === "string" ? data.status_code : "",
    status_label:
      typeof data.status_label === "string" ? data.status_label : "",
  };
}

export async function revokeFamilyStatusLink(
  shareLinkId: string,
): Promise<void> {
  const { error } = await invokeFunction<unknown>("family-status", {
    method: "POST",
    path: "/revoke",
    body: { share_link_id: shareLinkId },
  });

  if (error)
    throw toApiRequestError(error, "Failed to revoke family status link");
}
